import Anchor from "atoms/anchors/anchor";
import PublicationRecord from "models/view-models/publication-record";
import React, { PropsWithChildren, ReactNode, useState } from "react";
import useUpdatePublicationFavorite from "utilities/hooks/domain/user-publication-favorites/use-update-publication-favorite";
import { useLocalization } from "utilities/hooks/use-localization";
import StringUtils from "utilities/string-utils";
import PublicationListItemActions from "./publication-list-item-actions";
import { PublicationsListProps } from "./publications-list";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import { t } from "utilities/localization-utils";
import { ButtonStyles } from "atoms/constants/button-styles";
import usePublicationCover from "utilities/hooks/domain/publications/use-publication-cover";

const BASE_CLASS = "c-publication-selection-list-group-item";

export enum PublicationTextStyle {
    EditionFirst = "-edition-first",
    TitleFirst = "-title-first",
}

type SharedProps = Pick<
    PublicationsListProps,
    | "favorites"
    | "onChangeFavorites"
    | "onResultClick"
    | "showAsButton"
    | "hideActionMenu"
    | "onButtonClick"
>;

interface PublicationListItemProps extends SharedProps {
    isNested?: boolean;
    publication: PublicationRecord;
    style: PublicationTextStyle;
    children?: ReactNode;
}

const PublicationListItem: React.FunctionComponent<PublicationListItemProps> = (
    props
) => {
    const {
        children,
        favorites,
        isNested,
        onChangeFavorites,
        onResultClick = () => {},
        publication,
        style,
        hideActionMenu = false,
        showAsButton = false,
        onButtonClick = () => {},
    } = props;

    const [isFocused, setIsFocused] = useState(false);

    const { isFavorited } = useUpdatePublicationFavorite(
        publication,
        favorites,
        onChangeFavorites
    );

    const { coverImageSrc } = usePublicationCover(publication);

    const hasCoverImageSrc = StringUtils.hasValue(coverImageSrc);

    const modifierClasses: string[] = [];

    if (isFavorited) {
        modifierClasses.push(" -favorite");
    }

    if (isFocused) {
        modifierClasses.push(" -focused");
    }

    if (isNested) {
        modifierClasses.push(" -nested");
    }

    const Wrapper = ({ children }: PropsWithChildren) => {
        return showAsButton ? (
            <div className={`${BASE_CLASS}__item -noLink ${style.toString()}`}>
                {children}
            </div>
        ) : (
            <Anchor
                onClick={(e) => onResultClick()}
                cssClassName={`${BASE_CLASS}__item ${style.toString()}`}
                to={publication.getRoute()}>
                {children}
            </Anchor>
        );
    };

    return (
        <div
            onClick={() => setIsFocused(true)}
            onMouseLeave={() => setIsFocused(false)}
            className={`${BASE_CLASS} ${modifierClasses.join(" ")}`}>
            <div className={`${BASE_CLASS}__item_parent`}>
                <Wrapper>
                    {hasCoverImageSrc ? (
                        <img
                            className={`${BASE_CLASS}__cover`}
                            src={coverImageSrc}
                            alt={`${publication.getDisplayCodeEditionAndTitle()} cover`}
                            // @ts-ignore
                            loading="lazy"
                        />
                    ) : (
                        <div className={`${BASE_CLASS}__cover`} />
                    )}
                    <div>
                        <PublicationText
                            publication={publication}
                            style={style}>
                            {children}
                        </PublicationText>
                    </div>
                </Wrapper>
                {!hideActionMenu && (
                    <PublicationListItemActions
                        publication={publication}
                        favorites={favorites}
                        onChangeFavorites={onChangeFavorites}
                    />
                )}
                {showAsButton && (
                    <Button
                        cssClassName={`${BASE_CLASS}__selectButton`}
                        style={ButtonStyles.None}
                        onClick={() => onButtonClick(publication)}>
                        <Icon type={Icons.ChevronRight} />
                        <span className="sr-only">{t("view")}</span>
                    </Button>
                )}
            </div>
        </div>
    );
};

function PublicationText(
    props: React.PropsWithChildren<
        Pick<PublicationListItemProps, "publication" | "style">
    >
) {
    const { children, publication, style } = props;
    const { t } = useLocalization();

    const edition = (
        <div className={`${BASE_CLASS}__item__edition`}>
            {publication.edition} {t("edition")}
        </div>
    );

    const isBold = style === PublicationTextStyle.TitleFirst;

    const title = (
        <div className={`${BASE_CLASS}__item__title${isBold ? " -bold" : ""}`}>
            {publication.getDisplayTitle(false)}
        </div>
    );

    if (style === PublicationTextStyle.EditionFirst) {
        return (
            <React.Fragment>
                {edition}
                {title}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {title}
            <div className={`${BASE_CLASS}__toggle`}>
                {edition}
                {children}
            </div>
        </React.Fragment>
    );
}

export default PublicationListItem;
